import React, { FC, useEffect, useRef } from "react";

import { Box } from "@UI/Components/Box/Box";
import { Flex } from "@UI/Components/Flex/Flex";
import { Icon } from "@UI/Components/Icon/Icon";
import { Xmark } from "@UI/Components/Icons/Xmark";
import { Text } from "@UI/Components/Text/Text";
import { Textarea } from "@UI/Components/Textarea/Textarea";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import useI18n from "@I18n/hooks/useI18n";

import messages from "../../../messages";
import { RetryingImage } from "../../RetryingImage/RetryingImage";

export interface PhotoCardProps {
  photoUrl: string;
  description: string;
  onDescriptionChange: (value: string) => void;
  /** Called when the caption field is left after an actual edit, so the screen
   * can persist it. A focus/blur that changed nothing does not call it, so
   * tabbing through the cards saves nothing. */
  onDescriptionBlur?: () => void;
  captionMaxLength: number;
  onRequestRemove: () => void;
  /** Opens this photo in the full-size viewer. */
  onOpenViewer: () => void;
  /** True while *this* card's DELETE is in flight — it stays put and dims
   * rather than disappearing optimistically. */
  isRemoving?: boolean;
  /**
   * True while any card's DELETE is in flight, this one's included.
   *
   * The uploader tracks a single removal, so a second one started before the
   * first lands would clear its busy flag early and unlock the screen's
   * Continue over a photo the server is still deleting. Locking every × is
   * what keeps that from being reachable. Only the × — the caption field goes
   * by `isRemoving`, so another card's removal never interrupts typing.
   */
  isRemovalInFlight?: boolean;
  isDisabled?: boolean;
  /** Lifts the card off its background. The wizard doesn't need it — its page
   * is tinted, so a white card already separates — but a caller rendering this
   * on white does. */
  isElevated?: boolean;
}

/**
 * One attached photo: thumbnail, caption, remove button.
 *
 * The caption `Textarea` is always rendered at the same position in this
 * component's JSX regardless of whether the label/counter around it are
 * showing, so typing the first character (which makes them appear) never
 * remounts the field under the user's cursor.
 */
export const PhotoCard: FC<PhotoCardProps> = ({
  photoUrl,
  description,
  onDescriptionChange,
  onDescriptionBlur,
  captionMaxLength,
  onRequestRemove,
  onOpenViewer,
  isRemoving = false,
  isRemovalInFlight = false,
  isDisabled = false,
  isElevated = false,
}) => {
  const { formatMessage } = useI18n();
  const hasDescription = description.length > 0;
  // What the caption was when the field took focus — the only way to tell an
  // edit from a mere pass-through, since every keystroke has already been
  // reported by `onDescriptionChange`.
  const descriptionAtFocus = useRef(description);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // A plain textarea does not grow with its content, so a fixed `rows={2}`
  // would reserve a second, empty line under a one-line caption — the phantom
  // gap that used to strand the counter away from the text it counts. Sizing
  // it to `scrollHeight` on every change keeps it exactly as tall as what's
  // actually typed, one line or two.
  useEffect(() => {
    const node = textareaRef.current;
    if (!node) return;

    node.style.height = "auto";
    node.style.height = `${node.scrollHeight}px`;
  }, [description]);

  return (
    <Box
      position="relative"
      background="white"
      borderRadius="10px"
      padding="12px"
      boxShadow={isElevated ? "sm" : undefined}
      opacity={isRemoving ? 0.5 : 1}
    >
      <Box
        as="button"
        type="button"
        aria-label={formatMessage(messages["taskCreation.photos.removeButton"])}
        onClick={onRequestRemove}
        disabled={isDisabled || isRemoving || isRemovalInFlight}
        position="absolute"
        top="-8px"
        right="-8px"
        boxSize="24px"
        borderRadius="full"
        background="white"
        borderWidth="1px"
        borderColor="gray.200"
        boxShadow="sm"
        display="flex"
        alignItems="center"
        justifyContent="center"
        cursor="pointer"
        transition="transform 0.15s ease, background-color 0.15s ease, border-color 0.15s ease"
        _hover={{ transform: "scale(1.1)", background: "gray.100", borderColor: "gray.300" }}
      >
        <Icon as={Xmark} width="12px" height="12px" color="gray.900" />
      </Box>
      <Flex gap="12px" alignItems="center">
        <RetryingImage
          src={photoUrl}
          alt={formatMessage(messages["taskCreation.photos.thumbnailAlt"])}
          boxSize="64px"
          objectFit="cover"
          borderRadius="8px"
          flexShrink={0}
          cursor="pointer"
          onClick={onOpenViewer}
        />
        <Box flex="1" minWidth={0}>
          {hasDescription && (
            <Text fontSize="xs" color="gray.500" marginBottom="2px">
              <FormattedMessage {...messages["taskCreation.photos.descriptionLabel"]} />
            </Text>
          )}
          <Textarea
            ref={textareaRef}
            value={description}
            onChange={(event) => onDescriptionChange(event.target.value)}
            onFocus={() => {
              descriptionAtFocus.current = description;
            }}
            onBlur={() => {
              if (description !== descriptionAtFocus.current)
                onDescriptionBlur?.();
            }}
            placeholder={formatMessage(
              messages["taskCreation.photos.descriptionPlaceholder"]
            )}
            maxLength={captionMaxLength}
            isDisabled={isDisabled || isRemoving}
            variant="unstyled"
            resize="none"
            overflow="hidden"
            rows={1}
            fontSize="sm"
            borderRadius="0"
            borderBottom="1px solid"
            borderColor={hasDescription ? "gray.300" : "transparent"}
          />
          {hasDescription && (
            <Text fontSize="xs" color="gray.400" textAlign="right" marginTop="2px">
              {/* Digits and a slash — nothing to translate. */}
              {`${description.length}/${captionMaxLength}`}
            </Text>
          )}
        </Box>
      </Flex>
    </Box>
  );
};
